import React, { useEffect, useState } from 'react'
import { Button, Tooltip, message } from 'antd'

// What the loan officer can see and do about the Consumer Connect invitation
// for one loan. Three states, decided by the server:
//
//   - the sync has not produced an Encompass loan yet (nothing to invite to);
//   - the invitation went out and the borrower has not submitted (show the
//     link, let the LO copy it or resend it);
//   - the borrower submitted (show when, and the Encompass loan Consumer
//     Connect created; nothing left to send).
//
// Renders nothing for tenants without an Encompass integration.
export default function ConsumerConnectPanel({ loanId }) {
    const [status, setStatus] = useState(null)
    const [loading, setLoading] = useState(false)
    const [sending, setSending] = useState(false)

    const load = () => {
        if (!loanId) return
        setLoading(true)
        fetch('/los/consumerconnect/status?loan_id=' + encodeURIComponent(loanId), {
            headers: { Authorization: 'Bearer ' + sessionStorage.getItem('ZeitroA'), Cache: 'no-cache' },
        })
            .then(async (r) => (r.ok ? r.json() : null))
            .then((s) => setStatus(s))
            .catch(() => setStatus(null))
            .finally(() => setLoading(false))
    }

    useEffect(load, [loanId])

    // The LO usually opens the loan from the notification email, which goes
    // out before the pipeline has reached the invitation step. Keep asking
    // until the sync settles so the link appears without a manual reload.
    useEffect(() => {
        if (!status || !status.sync_active) return undefined
        const t = setTimeout(load, 5000)
        return () => clearTimeout(t)
    }, [status])

    if (!status || !status.applicable) return null

    const resend = () => {
        setSending(true)
        fetch('/los/consumerconnect/resend', {
            method: 'POST',
            headers: { Authorization: 'Bearer ' + sessionStorage.getItem('ZeitroA'), 'Content-Type': 'application/json' },
            body: JSON.stringify({ loan_id: loanId }),
        })
            .then(async (r) => {
                const text = await r.text()
                if (!r.ok) throw new Error(text || ('HTTP ' + r.status))
                const js = JSON.parse(text)
                message.success((js.regenerated ? 'New invitation sent to ' : 'Invitation resent to ') + js.sent_to)
                load()
            })
            .catch((e) => message.error(e.message))
            .finally(() => setSending(false))
    }

    const copy = () => {
        navigator.clipboard.writeText(status.invitation_url).then(
            () => message.success('Link copied'),
            () => message.error('Could not copy the link')
        )
    }

    const when = (iso) => (iso ? new Date(iso).toLocaleString() : '')

    let body
    if (status.submitted_at) {
        body = (
            <div className="info-item-value text-left">
                Submitted {when(status.submitted_at)}
                {status.consumer_connect_loan_id && (
                    <span style={{ color: '#6E6E70' }}> · Encompass loan {status.consumer_connect_loan_id}</span>
                )}
            </div>
        )
    } else if (!status.encompass_loan_id) {
        body = (
            <div className="info-item-value text-left" style={{ color: '#6E6E70' }}>
                {status.sync_status === 'failed_terminal'
                    ? 'The application could not be synced to Encompass, so no invitation was sent.'
                    : 'The invitation link will appear here once the application finishes syncing to Encompass.'}
            </div>
        )
    } else if (!status.invitation_url && status.sync_active) {
        body = (
            <div className="info-item-value text-left" style={{ color: '#6E6E70' }}>
                The application is still syncing to Encompass. The invitation is sent automatically when that finishes.
            </div>
        )
    } else {
        body = (
            <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                {status.invitation_url ? (
                    <Tooltip title={status.invitation_url} placement="top">
                        <div
                            className="info-item-value text-left"
                            style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                            {status.invitation_url}
                        </div>
                    </Tooltip>
                ) : (
                    <div className="info-item-value text-left" style={{ color: '#6E6E70' }}>
                        No invitation on file for this loan.
                    </div>
                )}
                {status.invitation_url && (
                    <img className="share-application-link-copy" src="/images/copy-blue.svg" alt="Copy" style={{ cursor: 'pointer' }} onClick={copy} />
                )}
                <Button type="primary" size="small" loading={sending} disabled={!status.can_resend} onClick={resend}>
                    {status.invitation_url ? 'Resend to borrower' : 'Send invitation'}
                </Button>
                {status.sent_at && (
                    <span style={{ color: '#6E6E70', fontSize: 12 }}>Last sent {when(status.sent_at)} · not submitted yet</span>
                )}
            </div>
        )
    }

    return (
        <div style={{ borderTop: '1px solid #ECECEC', padding: '16px 0' }}>
            <div className="info-item-label" style={{ marginBottom: 6 }}>
                Consumer Connect{loading ? ' …' : ''}
            </div>
            {body}
        </div>
    )
}
